import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  MessageSquare, Star, TrendingUp, History, Plus, Download, Share2,
  Filter, Search, ThumbsUp, ThumbsDown, Clock, CheckCircle, AlertCircle,
  Eye, Edit3, Trash2, Send, Calendar, BarChart3, PieChart, Users,
  Award, Target, Zap, Heart, Flag, Lightbulb, Bug, AlertTriangle, X,
  Smile, Frown, Meh
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Input } from '../../components/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';

const UnifiedFeedback = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'submit';
  
  // State from UserFeedbackSystem
  const [feedbackData, setFeedbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);

  // State from FeedbackCollection
  const [feedback, setFeedback] = useState({
    rating: 0,
    category: '',
    comment: '',
    satisfaction: '',
    improvement: '',
    recommend: '',
    contact: false,
    email: ''
  });
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  useEffect(() => {
    const fetchFeedbackData = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getUserFeedbackSystem();
        if (response.success) {
          setFeedbackData(response.data);
        } else {
          setError('Failed to load feedback data');
        }
      } catch (err) {
        setError('Error loading feedback data');
        console.error('Feedback data error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbackData();
    
    // Load feedback history from FeedbackCollection
    const mockHistory = [
      {
        id: 1,
        ticketId: "TKT-001",
        rating: 5,
        category: "Technical Support",
        comment: "Excellent support! The agent was very helpful and resolved my issue quickly.",
        satisfaction: "very_satisfied",
        date: "2025-01-15T14:30:00Z",
        status: "resolved"
      },
      {
        id: 2,
        ticketId: "TKT-002",
        rating: 4,
        category: "Billing",
        comment: "Good support overall, but took a bit longer than expected.",
        satisfaction: "satisfied",
        date: "2025-01-14T16:45:00Z",
        status: "resolved"
      },
      {
        id: 3,
        ticketId: "TKT-003",
        rating: 3,
        category: "General Inquiry",
        comment: "Average experience. Could be better.",
        satisfaction: "neutral",
        date: "2025-01-13T11:20:00Z",
        status: "resolved"
      }
    ];
    setFeedbackHistory(mockHistory);
  }, []);

  // Update URL when tab changes
  useEffect(() => {
    if (activeTab) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, setSearchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleRatingChange = (rating) => {
    setFeedback({ ...feedback, rating });
  };

  const handleInputChange = (field, value) => {
    setFeedback({ ...feedback, [field]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (feedback.rating === 0) {
      alert('Please provide a rating');
      return;
    }

    if (!feedback.category) {
      alert('Please select a category');
      return;
    }

    const newFeedback = {
      id: feedbackHistory.length + 1,
      ticketId: "TKT-004",
      ...feedback,
      date: new Date().toISOString(),
      status: "submitted"
    };

    setFeedbackHistory([newFeedback, ...feedbackHistory]);
    setIsSubmitted(true);
    setShowThankYou(true);
    
    setFeedback({
      rating: 0,
      category: '',
      comment: '',
      satisfaction: '',
      improvement: '',
      recommend: '',
      contact: false,
      email: ''
    });

    setTimeout(() => {
      setShowThankYou(false);
      setIsSubmitted(false);
    }, 3000);
  };

  const getRatingStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`h-8 w-8 cursor-pointer transition-colors ${
          index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
        onClick={() => handleRatingChange(index + 1)}
      />
    ));
  };

  const getSatisfactionIcon = (satisfaction) => {
    switch (satisfaction) {
      case 'very_satisfied': return <Smile className="h-6 w-6 text-green-600" />;
      case 'satisfied': return <Smile className="h-6 w-6 text-green-500" />;
      case 'neutral': return <Meh className="h-6 w-6 text-yellow-500" />;
      case 'dissatisfied': return <Frown className="h-6 w-6 text-orange-500" />;
      case 'very_dissatisfied': return <Frown className="h-6 w-6 text-red-500" />;
      default: return <Meh className="h-6 w-6 text-gray-400" />;
    }
  };

  const getSatisfactionColor = (satisfaction) => {
    switch (satisfaction) {
      case 'very_satisfied': return 'text-green-600 bg-green-100';
      case 'satisfied': return 'text-green-500 bg-green-100';
      case 'neutral': return 'text-yellow-500 bg-yellow-100';
      case 'dissatisfied': return 'text-orange-500 bg-orange-100';
      case 'very_dissatisfied': return 'text-red-500 bg-red-100';
      default: return 'text-gray-500 bg-gray-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'responded': return 'text-green-600 dark:text-green-400';
      case 'acknowledged': return 'text-blue-600 dark:text-blue-400';
      case 'under_review': return 'text-yellow-600 dark:text-yellow-400';
      case 'resolved': return 'text-green-600 dark:text-green-400';
      case 'submitted': return 'text-gray-600 dark:text-gray-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'responded': return CheckCircle;
      case 'acknowledged': return Clock;
      case 'under_review': return AlertCircle;
      case 'resolved': return CheckCircle;
      case 'submitted': return Clock;
      default: return Clock;
    }
  };

  const satisfactionOptions = [
    { value: 'very_satisfied', label: 'Very Satisfied', icon: <Smile className="h-5 w-5" /> },
    { value: 'satisfied', label: 'Satisfied', icon: <Smile className="h-5 w-5" /> },
    { value: 'neutral', label: 'Neutral', icon: <Meh className="h-5 w-5" /> },
    { value: 'dissatisfied', label: 'Dissatisfied', icon: <Frown className="h-5 w-5" /> },
    { value: 'very_dissatisfied', label: 'Very Dissatisfied', icon: <Frown className="h-5 w-5" /> }
  ];

  const categoryOptions = [
    'Technical Support',
    'Billing',
    'Account Management',
    'General Inquiry',
    'Feature Request',
    'Bug Report',
    'Training',
    'Other'
  ];

  const improvementOptions = [
    'Response Time',
    'Agent Knowledge',
    'Communication',
    'Resolution Quality',
    'Follow-up',
    'System Performance',
    'Documentation',
    'Other'
  ];

  const recommendOptions = [
    { value: 'definitely', label: 'Definitely', icon: <ThumbsUp className="h-5 w-5 text-green-600" /> },
    { value: 'probably', label: 'Probably', icon: <ThumbsUp className="h-5 w-5 text-green-500" /> },
    { value: 'maybe', label: 'Maybe', icon: <Meh className="h-5 w-5 text-yellow-500" /> },
    { value: 'probably_not', label: 'Probably Not', icon: <ThumbsDown className="h-5 w-5 text-orange-500" /> },
    { value: 'definitely_not', label: 'Definitely Not', icon: <ThumbsDown className="h-5 w-5 text-red-500" /> }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading feedback system...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  const { userFeedbackSystem, feedbackStats, recentTickets } = feedbackData || {};
  const { feedbackHistory: systemFeedbackHistory = [] } = userFeedbackSystem || {};

  const tabs = [
    { id: 'submit', label: 'Submit Feedback', icon: Plus },
    { id: 'my-feedback', label: 'My Feedback', icon: History },
    { id: 'overview', label: 'Overview', icon: BarChart3 }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header (from UserFeedbackSystem) */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <MessageSquare className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Feedback System
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Share your feedback to help us improve
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleTabChange('submit')}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>New Feedback</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Feedback Stats (from UserFeedbackSystem) */}
        {feedbackStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <MessageSquare className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Feedback</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{feedbackStats.totalFeedback}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <Star className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Rating</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{feedbackStats.averageRating}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <Target className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Satisfaction Score</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{feedbackStats.satisfactionScore}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Response Rate</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{feedbackStats.responseRate}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Feedback Sections</h3>
                <nav className="space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeTab === tab.id
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card>
              {/* Submit Feedback Tab (from FeedbackCollection) */}
              {activeTab === 'submit' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Submit Feedback</h2>
                  </div>

                  {/* Thank You Message */}
                  {showThankYou && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md mx-4 text-center">
                        <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Thank You!</h2>
                        <p className="text-gray-600 dark:text-gray-400">Your feedback has been submitted successfully.</p>
                        <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">We appreciate your time and input.</p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Rating */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Overall Rating *
                      </label>
                      <div className="flex items-center gap-2">
                        {getRatingStars(feedback.rating)}
                        <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                          {feedback.rating > 0 ? `${feedback.rating} out of 5` : 'Please rate'}
                        </span>
                      </div>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category *
                      </label>
                      <Select value={feedback.category} onValueChange={(value) => handleInputChange('category', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoryOptions.map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Satisfaction Level */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        How satisfied are you with our support?
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {satisfactionOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`p-3 rounded-lg border text-left transition-colors ${
                              feedback.satisfaction === option.value
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                            onClick={() => handleInputChange('satisfaction', option.value)}
                          >
                            <div className="flex items-center gap-2">
                              {option.icon}
                              <span className="text-sm font-medium">{option.label}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Comment */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Additional Comments
                      </label>
                      <textarea
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        rows={4}
                        placeholder="Tell us more about your experience..."
                        value={feedback.comment}
                        onChange={(e) => handleInputChange('comment', e.target.value)}
                      />
                    </div>

                    {/* Improvement Areas */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        What could we improve?
                      </label>
                      <Select value={feedback.improvement} onValueChange={(value) => handleInputChange('improvement', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an area" />
                        </SelectTrigger>
                        <SelectContent>
                          {improvementOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Recommendation */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Would you recommend our support to others?
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {recommendOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`p-3 rounded-lg border text-left transition-colors ${
                              feedback.recommend === option.value
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                            onClick={() => handleInputChange('recommend', option.value)}
                          >
                            <div className="flex items-center gap-2">
                              {option.icon}
                              <span className="text-sm font-medium">{option.label}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Contact Preference */}
                    <div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={feedback.contact}
                          onChange={(e) => handleInputChange('contact', e.target.checked)}
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          I'd like to be contacted for follow-up
                        </span>
                      </label>
                      {feedback.contact && (
                        <div className="mt-2">
                          <Input
                            type="email"
                            placeholder="Enter your email address"
                            value={feedback.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="mt-2"
                          />
                        </div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <Button type="submit" className="w-full" disabled={isSubmitted}>
                      {isSubmitted ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Submit Feedback
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              )}

              {/* My Feedback Tab (from UserFeedbackSystem) */}
              {activeTab === 'my-feedback' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">My Feedback</h2>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                        <Download className="h-4 w-4" />
                        <span>Export</span>
                      </button>
                      <button className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                        <Filter className="h-4 w-4" />
                        <span>Filter</span>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {feedbackHistory.length > 0 ? (
                      feedbackHistory.map((item) => {
                        const StatusIcon = getStatusIcon(item.status);
                        return (
                          <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{item.ticketId}</Badge>
                                <Badge className={getSatisfactionColor(item.satisfaction)}>
                                  {getSatisfactionIcon(item.satisfaction)}
                                </Badge>
                              </div>
                              <div className="flex items-center space-x-2">
                                <StatusIcon className={`h-4 w-4 ${getStatusColor(item.status)}`} />
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  {new Date(item.date).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex">
                                {Array.from({ length: 5 }, (_, index) => (
                                  <Star
                                    key={index}
                                    className={`h-4 w-4 ${
                                      index < item.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">{item.category}</span>
                            </div>
                            {item.comment && (
                              <p className="text-sm text-gray-700 dark:text-gray-300">{item.comment}</p>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8">
                        <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-400">No feedback submitted yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Overview Tab (from UserFeedbackSystem) */}
              {activeTab === 'overview' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Feedback Overview</h2>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                        <Download className="h-4 w-4" />
                        <span>Export</span>
                      </button>
                      <button className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                        <Share2 className="h-4 w-4" />
                        <span>Share</span>
                      </button>
                    </div>
                  </div>

                  {/* Recent Tickets for Feedback */}
                  {recentTickets && recentTickets.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Recent Tickets - Provide Feedback</h3>
                      <div className="space-y-3">
                        {recentTickets.slice(0, 5).map((ticket) => (
                          <div key={ticket.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex items-center space-x-4">
                              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{ticket.title}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Resolved by {ticket.agentName} • {new Date(ticket.resolvedDate).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleTabChange('submit')}
                              className="px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors"
                            >
                              Provide Feedback
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Feedback */}
                  {systemFeedbackHistory && systemFeedbackHistory.length > 0 && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Recent Feedback</h3>
                      <div className="space-y-3">
                        {systemFeedbackHistory.slice(0, 5).map((feedback) => {
                          const StatusIcon = getStatusIcon(feedback.status);
                          return (
                            <div key={feedback.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <div className="flex items-center space-x-4">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                  <MessageSquare className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{feedback.title}</p>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {feedback.type === 'ticket' ? 'Ticket Feedback' : 'Service Feedback'} • {new Date(feedback.submittedDate).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {feedback.rating && (
                                  <div className="flex items-center space-x-1">
                                    <Star className="h-4 w-4 text-yellow-500 fill-current" />
                                    <span className="text-sm text-gray-900 dark:text-gray-100">{feedback.rating}</span>
                                  </div>
                                )}
                                <StatusIcon className={`h-4 w-4 ${getStatusColor(feedback.status)}`} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedFeedback;

